import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import API from '@/lib/api';
import { User, Mail, Phone, Home, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export default function Profile() {
  const { user, logout } = useAuth();
  const [form, setForm] = useState({ name: user?.name || '', phone: user?.phone || '', unit: user?.unit || '' });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await API.put('/profile', form);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch { /* ignore */ }
    setSaving(false);
  };

  return (
    <div className="fade-in max-w-xl mx-auto space-y-8" data-testid="profile-page">
      <h1 className="text-3xl font-extrabold tracking-tight text-[#222] font-[Manrope]">Profile</h1>

      <div className="flex items-center gap-4">
        {user?.avatar ? (
          <img src={user.avatar} alt={user.name} className="w-20 h-20 rounded-2xl object-cover border border-[#EBEBEB]" />
        ) : (
          <div className="w-20 h-20 rounded-2xl bg-[#F7F7F7] flex items-center justify-center">
            <User className="w-8 h-8 text-[#717171]" strokeWidth={1.5} />
          </div>
        )}
        <div>
          <h2 className="text-xl font-bold text-[#222] font-[Manrope]">{user?.name}</h2>
          <p className="text-sm text-[#717171] capitalize">{user?.role}</p>
          <p className="text-sm text-[#717171]">{user?.email}</p>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-[#EBEBEB] p-6 space-y-4">
        <div>
          <Label className="text-sm font-medium text-[#222] flex items-center gap-1.5"><User className="w-3.5 h-3.5" strokeWidth={1.5} />Name</Label>
          <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="mt-1.5 rounded-lg border-[#EBEBEB] h-11" data-testid="profile-name" />
        </div>
        <div>
          <Label className="text-sm font-medium text-[#222] flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" strokeWidth={1.5} />Email</Label>
          <Input value={user?.email || ''} disabled className="mt-1.5 rounded-lg border-[#EBEBEB] h-11 bg-[#F7F7F7]" data-testid="profile-email" />
        </div>
        <div>
          <Label className="text-sm font-medium text-[#222] flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" strokeWidth={1.5} />Phone</Label>
          <Input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="(555) 000-0000" className="mt-1.5 rounded-lg border-[#EBEBEB] h-11" data-testid="profile-phone" />
        </div>
        {user?.role === 'resident' && (
          <div>
            <Label className="text-sm font-medium text-[#222] flex items-center gap-1.5"><Home className="w-3.5 h-3.5" strokeWidth={1.5} />Unit</Label>
            <Input value={form.unit} onChange={e => setForm(f => ({ ...f, unit: e.target.value }))} placeholder="e.g. PH-2401" className="mt-1.5 rounded-lg border-[#EBEBEB] h-11" data-testid="profile-unit" />
          </div>
        )}
        <Button onClick={handleSave} disabled={saving} className="bg-[#E05A47] hover:bg-[#C54A39] text-white rounded-lg font-semibold gap-2 btn-press" data-testid="save-profile-btn">
          {saving ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : saved ? <><Save className="w-4 h-4" /> Saved!</> : <><Save className="w-4 h-4" /> Save Changes</>}
        </Button>
      </div>
    </div>
  );
}
